import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/anim";
import { EASE } from "@/components/anim";

const FAQS = [
  {
    q: "How do I book the space?",
    a: "Submit a booking inquiry through our Book page with your preferred rental type and dates. Essandtials will contact you to confirm your booking — it becomes official once payment and the rental agreement are received.",
  },
  {
    q: "Can I rent Essandtials for one day?",
    a: "Yes. The 1-Day Pop-Up starts at $250 and is perfect for launches, test runs, and single-day shopping events.",
  },
  {
    q: "Can online businesses rent a rack instead of the entire store?",
    a: "Absolutely. Our Shop Within a Shop options include Rack Residency ($250/month), Premium Rack + Display ($350/month), Shelf Residency ($150/month), Tabletop Display ($125/month), and Window Feature ($200/week).",
  },
  {
    q: "Can Essandtials sell my products for me?",
    a: "Yes — that's our Managed Retail option. You supply the inventory and Essandtials handles customer sales during store operating hours for an additional management fee or commission.",
  },
  {
    q: "Can I rent a makeup chair?",
    a: "Yes. Makeup chairs rent for $50/day, $225/week, or $650/month, and include a professional makeup chair, mirror, lighting area, workspace, and access to shared facilities where applicable.",
  },
  {
    q: "Can I rent a hair chair?",
    a: "Yes. Hair chairs rent for $75/day, $325/week, or $850/month. Approved services are carried out from a professional and stylish workspace.",
  },
  {
    q: "Can I rent a nail station?",
    a: "Yes. Nail stations rent for $60/day, $275/week, or $750/month — suitable for manicures and approved nail services.",
  },
  {
    q: "Do I need my own equipment?",
    a: "Stations include the essentials (chair, mirror, lighting, workspace). Professionals should provide their own specialized tools, supplies, and products unless specifically included in your rental.",
  },
  {
    q: "Can I bring my own signage?",
    a: "Yes, temporary branding and signage are welcome — they just need to be approved by Essandtials first.",
  },
  {
    q: "Can I decorate the store?",
    a: "Yes, temporarily. No permanent alterations, drilling, painting, or damaging adhesives are permitted, and displays must be approved.",
  },
  {
    q: "Can I host an event?",
    a: "Yes — product launches, Sip & Shops, trunk shows, workshops, networking events, beauty events, private shopping events, brand activations, content creation, and more. See our Events page for inspiration.",
  },
  {
    q: "Is Wi-Fi included?",
    a: "Yes. Wi-Fi is included along with lighting, a checkout area, clothing racks, shelving, mirrors, a fitting area, and display furniture.",
  },
  {
    q: "Is there a security deposit?",
    a: "A refundable security deposit may be required depending on your rental. If so, it will be clearly outlined in your rental agreement.",
  },
  {
    q: "What happens if I damage something?",
    a: "Damage, excessive cleaning, missing fixtures, or missing equipment may be deducted from the security deposit. Renters are responsible for damage caused by dyes, polish, adhesives, chemicals, hot tools, or equipment.",
  },
  {
    q: "What is the cancellation policy?",
    a: "Cancellation terms are set out in your rental agreement. Contact us as early as possible and we'll walk you through the terms that apply to your dates.",
  },
  {
    q: "Can I book recurring days each month?",
    a: "Yes. Monthly Retail Residencies, monthly beauty rentals, and Beauty Flex Passes (like the 4-Day Flex Pass) are designed for recurring bookings.",
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={index * 0.02}>
      <div className="border-b border-beige/60" data-testid={`faq-item-${index + 1}`}>
        <button
          data-testid={`faq-question-${index + 1}`}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-6 py-6 text-left"
        >
          <span className="font-serif text-xl text-ink sm:text-2xl">{item.q}</span>
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-beige transition-all duration-300 ${
              open ? "rotate-45 bg-ink text-paper" : "text-ink"
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="overflow-hidden"
            >
              <p className="max-w-3xl pb-7 text-sm leading-relaxed text-clay sm:text-base">
                {item.a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}

export default function Faq() {
  return (
    <>
      <PageHero
        eyebrow="FAQ"
        lines={[
          <>Everything,</>,
          <>
            <em>answered.</em>
          </>,
        ]}
        sub="The questions we hear most — answered. Anything else? Email rentals@essandtials.com or DM us on Instagram."
      />
      <section className="bg-paper pb-24 sm:pb-32" data-testid="faq-list">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          {FAQS.map((f, i) => (
            <FaqItem key={i} item={f} index={i} />
          ))}
          <Reveal delay={0.1}>
            <p className="mt-12 text-center text-sm text-clay">
              Still have questions?{" "}
              <Link to="/book" className="underline underline-offset-4 hover:text-ink">
                Send us a booking inquiry
              </Link>{" "}
              and we&rsquo;ll respond personally.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
